import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Lock, Trash2, AlertTriangle } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Button } from "../../../../components/ui/Button";
import { Select } from "../../../../components/ui/Select";
import { Textarea } from "../../../../components/ui/Textarea";
import {
  FOLDER_COLOR_STYLES,
  DEFAULT_FOLDER_COLOR,
} from "../../../../components/helpdesk/folderColors";
import {
  ACTION_DEFS,
  MAX_ACTIONS,
  MAX_LABELS_PER_ACTION,
  NO_FOLDER,
  SELF_AGENT,
  TEXT_MAX,
  RESOLVE_NOTE_MAX,
  defaultParams,
  idOf,
  normalizeMacroActions,
  validateAction,
} from "./workflowSchema";

// Editor de pasos ordenados, compartido por macros y reglas. Cada paso es una
// fila: número, tipo, sus parámetros y los controles de orden. En macros,
// "Resolver el caso" queda fijo al final (normalizeMacroActions se aplica a
// cada cambio): el orden inválido no se puede ni dibujar.
//
// `actions` lleva `_key` local por paso para que React no confunda filas al
// reordenar; serializeAction lo descarta al enviar.

let stepSeq = 0;
export function withStepKey(action) {
  stepSeq += 1;
  return { ...action, params: { ...(action.params || {}) }, _key: `step-${stepSeq}` };
}

// ── Selector múltiple de etiquetas (chips conmutables) ───────────────────────
export function LabelToggleList({ labels, selected, onChange, max, idPrefix }) {
  const { t } = useTranslation();
  if (!labels || labels.length === 0) {
    return (
      <p className="text-xs text-fg-muted">
        {t("Todavía no hay etiquetas. Créalas en la pestaña Etiquetas.")}
      </p>
    );
  }
  const chosen = new Set(selected || []);
  const full = max != null && chosen.size >= max;
  return (
    <div role="group" aria-label={t("Etiquetas")} className="flex flex-wrap gap-1.5" id={idPrefix}>
      {labels.map((label) => {
        const id = idOf(label);
        const active = chosen.has(id);
        const styles = FOLDER_COLOR_STYLES[label.color] || FOLDER_COLOR_STYLES[DEFAULT_FOLDER_COLOR];
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            disabled={!active && full}
            onClick={() => {
              const next = new Set(chosen);
              if (active) next.delete(id);
              else next.add(id);
              onChange([...next]);
            }}
            className={cn(
              "inline-flex items-center gap-1.5 h-11 md:h-7 px-2.5 rounded-full border text-xs font-medium",
              "transition-colors duration-150 ease-out motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              active
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-border bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg",
            )}
          >
            <span className={cn("w-2 h-2 rounded-full shrink-0", styles.dot)} aria-hidden="true" />
            <span className="truncate max-w-[180px]">{label.name}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Aviso de catálogo que no cargó ───────────────────────────────────────────
function CatalogError({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-status-error">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      {message}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="font-medium text-brand-700 underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          {t("Reintentar")}
        </button>
      )}
    </p>
  );
}

// ── Parámetros de un paso ────────────────────────────────────────────────────
function StepParams({ action, onParams, mode, labels, catalogs, catalogErrors, onRetryCatalog, fieldId }) {
  const { t } = useTranslation();
  const params = action.params || {};

  switch (action.type) {
    case "add_labels":
    case "remove_labels":
      return (
        <LabelToggleList
          idPrefix={fieldId}
          labels={labels}
          selected={params.label_ids}
          max={MAX_LABELS_PER_ACTION}
          onChange={(label_ids) => onParams({ label_ids })}
        />
      );

    case "assign": {
      const team = catalogs.team;
      return (
        <div className="space-y-1.5">
          <Select
            id={fieldId}
            aria-label={t("Asignar a")}
            value={params.agent_id || ""}
            disabled={team === null}
            onChange={(e) => onParams({ agent_id: e.target.value })}
            placeholder={team === null ? t("Cargando equipo…") : t("Elige a una persona")}
          >
            {/* "Yo mismo" solo existe en macros: en una regla no hay nadie
                ejecutándola (el actor es la propia regla). */}
            {mode === "macro" && (
              <option value={SELF_AGENT}>{t("Yo mismo (quien ejecuta la macro)")}</option>
            )}
            {(team || []).map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
            {/* Asignado a alguien que ya no figura en el equipo: se conserva el
                valor para no perderlo en silencio al guardar. */}
            {params.agent_id &&
              params.agent_id !== SELF_AGENT &&
              team &&
              !team.some((m) => m.id === params.agent_id) && (
                <option value={params.agent_id}>{t("Persona que ya no está en el equipo")}</option>
              )}
          </Select>
          {catalogErrors.team && (
            <CatalogError message={t("No se pudo cargar el equipo.")} onRetry={() => onRetryCatalog("team")} />
          )}
        </div>
      );
    }

    case "set_folder": {
      const folders = catalogs.folders;
      return (
        <div className="space-y-1.5">
          <Select
            id={fieldId}
            aria-label={t("Mover a carpeta")}
            value={params.folder_id || NO_FOLDER}
            disabled={folders === null}
            onChange={(e) =>
              onParams({ folder_id: e.target.value === NO_FOLDER ? null : e.target.value })
            }
          >
            <option value={NO_FOLDER}>{t("Sin carpeta (sacarla de su carpeta)")}</option>
            {(folders || []).map((folder) => (
              <option key={idOf(folder)} value={idOf(folder)}>
                {folder.name}
              </option>
            ))}
            {params.folder_id &&
              folders &&
              !folders.some((f) => idOf(f) === params.folder_id) && (
                <option value={params.folder_id}>{t("Carpeta eliminada")}</option>
              )}
          </Select>
          {catalogErrors.folders && (
            <CatalogError
              message={t("No se pudieron cargar las carpetas.")}
              onRetry={() => onRetryCatalog("folders")}
            />
          )}
        </div>
      );
    }

    case "add_note":
    case "send_message": {
      const isMessage = action.type === "send_message";
      const quickReplies = catalogs.quickReplies || [];
      return (
        <div className="space-y-1.5">
          {isMessage && quickReplies.length > 0 && (
            <Select
              aria-label={t("Usar una respuesta rápida")}
              value=""
              onChange={(e) => {
                const reply = quickReplies.find((r) => String(idOf(r)) === e.target.value);
                if (reply) onParams({ content: reply.body || "" });
              }}
              placeholder={t("Usar una respuesta rápida…")}
              options={quickReplies.map((r) => ({ value: String(idOf(r)), label: r.title }))}
            />
          )}
          <Textarea
            id={fieldId}
            rows={3}
            maxLength={TEXT_MAX}
            value={params.content || ""}
            aria-label={isMessage ? t("Mensaje al contacto") : t("Texto de la nota")}
            placeholder={
              isMessage
                ? t("Lo que se le envía al contacto, con tu firma…")
                : t("Solo la ve el equipo; nunca llega al contacto ni a la IA…")
            }
            onChange={(e) => onParams({ content: e.target.value })}
          />
        </div>
      );
    }

    case "resolve":
      return (
        <Textarea
          id={fieldId}
          rows={2}
          maxLength={RESOLVE_NOTE_MAX}
          value={params.note || ""}
          aria-label={t("Nota de cierre (opcional)")}
          placeholder={t("Nota de cierre (opcional)")}
          onChange={(e) => onParams({ note: e.target.value })}
        />
      );

    default:
      return null;
  }
}

/**
 * @param {object} props
 * @param {Array<{_key:string,type:string,params:object}>} props.actions
 * @param {(next:Array)=>void} props.onChange
 * @param {string[]} props.allowedTypes  tipos ofrecidos al agregar un paso
 * @param {'macro'|'rule'} props.mode
 * @param {Array} props.labels
 * @param {{team:Array|null, folders:Array|null, quickReplies:Array|null}} props.catalogs
 * @param {Record<string,boolean>} props.catalogErrors
 * @param {(source:string)=>void} props.onRetryCatalog
 * @param {boolean} props.showErrors  mostrar errores de cada paso (tras intentar guardar)
 * @param {string} props.idPrefix
 */
export default function ActionStepsEditor({
  actions,
  onChange,
  allowedTypes,
  mode,
  labels,
  catalogs,
  catalogErrors,
  onRetryCatalog,
  showErrors,
  idPrefix,
}) {
  const { t } = useTranslation();
  const commit = (next) => onChange(mode === "macro" ? normalizeMacroActions(next) : next);

  const hasResolve = actions.some((a) => a.type === "resolve");
  // El último índice que se puede mover: con "resolver" presente, el paso
  // anterior a él no puede bajar más.
  const lastMovable = hasResolve ? actions.length - 2 : actions.length - 1;
  const full = actions.length >= MAX_ACTIONS;

  const addStep = (type) => {
    if (!type || full || !allowedTypes.includes(type)) return;
    commit([...actions, withStepKey({ type, params: defaultParams(type) })]);
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target > lastMovable) return;
    const next = [...actions];
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };

  const remove = (index) => commit(actions.filter((_, i) => i !== index));

  const setParams = (index, patch) =>
    commit(
      actions.map((a, i) => (i === index ? { ...a, params: { ...a.params, ...patch } } : a)),
    );

  const addOptions = allowedTypes.map((type) => ({
    value: type,
    label: t(ACTION_DEFS[type].label),
    // Una sola acción de cierre por macro.
    disabled: type === "resolve" && hasResolve,
  }));

  return (
    <div className="space-y-2">
      {actions.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-fg-muted">
          {t("Sin acciones todavía. Agrega la primera abajo.")}
        </p>
      )}

      <ol className="space-y-2" aria-label={t("Acciones en orden")}>
        {actions.map((action, index) => {
          const isResolve = action.type === "resolve";
          const error = showErrors ? validateAction(action, t) : null;
          const fieldId = `${idPrefix}-${action._key}`;
          const def = ACTION_DEFS[action.type];
          return (
            <li
              key={action._key}
              className={cn(
                "rounded-lg border bg-bg p-3",
                error ? "border-status-error" : "border-border",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-bg-muted text-[11px] font-semibold text-fg-muted tabular-nums shrink-0"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <label htmlFor={fieldId} className="flex-1 min-w-0 text-sm font-medium text-fg truncate">
                  {def ? t(def.label) : action.type}
                </label>
                {isResolve && mode === "macro" ? (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-fg-muted shrink-0"
                    title={t("Resolver siempre va al final: después del cierre no se ejecuta nada más.")}
                  >
                    <Lock className="w-3.5 h-3.5" aria-hidden="true" />
                    {t("Siempre al final")}
                  </span>
                ) : (
                  <span className="flex items-center shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={t("Subir acción")}
                    >
                      <ArrowUp className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8"
                      disabled={index >= lastMovable}
                      onClick={() => move(index, 1)}
                      aria-label={t("Bajar acción")}
                    >
                      <ArrowDown className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2 h-11 md:h-8 text-status-error hover:bg-status-error-soft shrink-0"
                  onClick={() => remove(index)}
                  aria-label={t("Quitar acción")}
                >
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
              <div className="mt-2 pl-0 md:pl-8">
                <StepParams
                  action={action}
                  onParams={(patch) => setParams(index, patch)}
                  mode={mode}
                  labels={labels}
                  catalogs={catalogs}
                  catalogErrors={catalogErrors}
                  onRetryCatalog={onRetryCatalog}
                  fieldId={fieldId}
                />
                {error && <p className="mt-1.5 text-xs text-status-error">{error}</p>}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <Select
            aria-label={t("Agregar acción")}
            value=""
            disabled={full}
            onChange={(e) => addStep(e.target.value)}
            placeholder={full ? t("Tope de 10 acciones") : t("+ Agregar acción…")}
            options={addOptions}
          />
        </div>
        <span className="text-xs text-fg-muted tabular-nums">
          {actions.length} / {MAX_ACTIONS}
        </span>
      </div>
    </div>
  );
}
